import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";

const TRIAL_DAYS = 15;
const PAID_PLAN_DAYS = 365;

// SignupPlanControl: per-user snapshot assigned at signup.
// Legacy users have no initialPlanDays → fall back to 15/365 so nothing changes.
const resolveBaseDays = (planTier, user, profile) => {
  const snap = parseInt(
    profile?.initialPlanDays ?? user?.initialPlanDays ?? NaN,
    10,
  );
  if (Number.isFinite(snap) && snap > 0) return snap;
  return planTier === 'silver' || planTier === 'gold' ? PAID_PLAN_DAYS : TRIAL_DAYS;
};

// Define which pages each plan can access
const PLAN_PERMISSIONS = {
  // Silver plan has limited access
  silver: [
    "/dashboard",
    "/services",
    "/team",
    "/events",
    "/projects",
    "/settings",
    "/payment", // Always allow payment page for upgrades
  ],
  // Gold plan has full access
  gold: [
    "/dashboard",
    "/services",
    "/team",
    "/events",
    "/projects",
    "/settings",
    "/quotations",
    "/billing",
    "/policy",
    "/payment",
  ],
  // Free/trial users have same access as silver during trial
  free: [
    "/dashboard",
    "/services",
    "/team",
    "/events",
    "/projects",
    "/settings",
    "/payment",
  ],
  none: [
    "/dashboard",
    "/services",
    "/team",
    "/events",
    "/projects",
    "/settings",
    "/payment",
  ],
};

// Pages that require Gold plan
const GOLD_ONLY_PAGES = ["/quotations", "/billing", "/policy"];

/**
 * Hook to check user's trial status and plan permissions
 * Returns information about whether trial is active, expired, days remaining, etc.
 */
const useTrialStatus = () => {
  const { user } = useSelector((state) => state.auth);
  const { profile } = useSelector((state) => state.settings || {});
  const [currentTimeMs, setCurrentTimeMs] = useState(() => Date.now());

  const nextExpiryTimeMs = useMemo(() => {
    const rawPlan =
      (profile && (profile.plan || profile.subscription?.tier)) ||
      user?.plan ||
      "free";
    const planTier = String(rawPlan).toLowerCase();
    const hasPaidPlan = planTier === "silver" || planTier === "gold";

    if (hasPaidPlan) {
      const subscription = profile?.subscription || user?.subscription || null;
      const expiresAt =
        subscription?.expiresAt ||
        subscription?.endDate ||
        profile?.subscription?.endDate ||
        null;

      if (expiresAt) {
        const expiryMs = new Date(expiresAt).getTime();
        return Number.isNaN(expiryMs) ? null : expiryMs;
      }

      const planActivatedAt = profile?.planActivatedAt || user?.planActivatedAt;
      if (planActivatedAt) {
        const activatedMs = new Date(planActivatedAt).getTime();
        if (!Number.isNaN(activatedMs)) {
          const extraDays = parseInt(
            profile?.planExtraDays || user?.planExtraDays || 0,
            10,
          );
          const baseDays = resolveBaseDays(planTier, user, profile);
          return (
            activatedMs + (baseDays + extraDays) * 24 * 60 * 60 * 1000
          );
        }
      }

      return null;
    }

    const createdAt = user?.createdAt || profile?.createdAt;
    if (!createdAt) return null;

    const createdMs = new Date(createdAt).getTime();
    if (Number.isNaN(createdMs)) return null;

    const extraDays = parseInt(
      profile?.planExtraDays || user?.planExtraDays || 0,
      10,
    );
    const baseDays = resolveBaseDays(planTier, user, profile);
    return createdMs + (baseDays + extraDays) * 24 * 60 * 60 * 1000;
  }, [user, profile]);

  useEffect(() => {
    const heartbeatTimer = setInterval(() => {
      setCurrentTimeMs(Date.now());
    }, 60000);

    let expiryTimer = null;
    if (nextExpiryTimeMs && nextExpiryTimeMs > Date.now()) {
      expiryTimer = setTimeout(
        () => {
          setCurrentTimeMs(Date.now());
        },
        Math.max(0, nextExpiryTimeMs - Date.now()),
      );
    }

    return () => {
      clearInterval(heartbeatTimer);
      if (expiryTimer) clearTimeout(expiryTimer);
    };
  }, [nextExpiryTimeMs]);

  const trialStatus = useMemo(() => {
    const rawPlan =
      (profile && (profile.plan || profile.subscription?.tier)) ||
      user?.plan ||
      "free";
    const planTier = String(rawPlan).toLowerCase();
    const hasPaidPlan = planTier === "silver" || planTier === "gold";
    const isGoldPlan = planTier === "gold";
    const isSilverPlan = planTier === "silver";
    const subscription = profile?.subscription || user?.subscription || null;

    const planPages = PLAN_PERMISSIONS[planTier] || PLAN_PERMISSIONS.free;

    const canAccessPage = (path, isExpired) => {
      if (path === "/payment") return true;

      if (isExpired) return false;

      return planPages.some(
        (allowed) => path === allowed || path.startsWith(allowed + "/"),
      );
    };

    const requiresGoldPlan = (path) => {
      return GOLD_ONLY_PAGES.some(
        (goldPage) => path === goldPage || path.startsWith(goldPage + "/"),
      );
    };

    // Admin / super admin: always active gold plan, never expires
    const userRole = String(user?.role || profile?.role || "").toLowerCase();
    if (userRole === "admin" || userRole === "superadmin") {
      return {
        isOnTrial: false,
        isExpired: false,
        hasPaidPlan: true,
        hasActivePlan: true,
        isGoldPlan: true,
        isSilverPlan: false,
        daysRemaining: 9999,
        daysUsed: 0,
        planTier: "gold",
        planDurationDays: null,
        expiresAt: null,
        canUseFeatures: true,
        allowedPages: PLAN_PERMISSIONS.gold,
        canAccessPage: () => true,
        requiresGoldPlan: () => false,
        goldOnlyPages: GOLD_ONLY_PAGES,
      };
    }

    if (hasPaidPlan) {
      let daysRemaining = resolveBaseDays(planTier, user, profile);
      let daysUsed = 0;
      let isExpired = false;
      let expiresAt = subscription?.expiresAt || null;
      const planActivatedAt = profile?.planActivatedAt || user?.planActivatedAt;

      if (subscription?.daysRemaining !== undefined) {
        daysRemaining = Math.max(0, Number(subscription.daysRemaining) || 0);
      }

      if (subscription?.daysUsed !== undefined) {
        daysUsed = Math.max(0, Number(subscription.daysUsed) || 0);
      }

      if (subscription?.isExpired === true) {
        isExpired = true;
      }

      if (!subscription && planActivatedAt) {
        const extraDays = parseInt(
          profile?.planExtraDays || user?.planExtraDays || 0,
          10,
        );
        const baseDays = resolveBaseDays(planTier, user, profile);
        const activationDate = new Date(planActivatedAt);
        const expiryDate = new Date(activationDate);
        expiryDate.setDate(expiryDate.getDate() + baseDays + extraDays);

        const now = new Date(currentTimeMs);
        const diff = expiryDate - now;
        daysRemaining = Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)));
        daysUsed = Math.max(
          0,
          Math.floor((now - activationDate) / (1000 * 60 * 60 * 24)),
        );
        expiresAt = expiryDate.toISOString();
      } else if (profile?.subscription?.daysRemaining !== undefined) {
        daysRemaining = profile.subscription.daysRemaining;
      } else if (profile?.subscription?.endDate) {
        const end = new Date(profile.subscription.endDate);
        const diff = end - new Date(currentTimeMs);
        daysRemaining = Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)));
        expiresAt = end.toISOString();
      }

      if (!isExpired) {
        isExpired = daysRemaining <= 0;
      }

      const allowedPages = isExpired ? ["/payment"] : planPages;

      return {
        isOnTrial: false,
        isExpired,
        hasPaidPlan: true,
        hasActivePlan: !isExpired,
        isGoldPlan,
        isSilverPlan,
        daysRemaining,
        daysUsed,
        planTier,
        planDurationDays: resolveBaseDays(planTier, user, profile),
        expiresAt,
        canUseFeatures: !isExpired,
        allowedPages,
        canAccessPage: (path) => canAccessPage(path, isExpired),
        requiresGoldPlan,
        goldOnlyPages: GOLD_ONLY_PAGES,
      };
    }

    const createdAt = user?.createdAt || profile?.createdAt;

    if (!createdAt) {
      const fallbackBase = resolveBaseDays(planTier, user, profile);
      return {
        isOnTrial: true,
        isExpired: false,
        hasPaidPlan: false,
        hasActivePlan: false,
        isGoldPlan: false,
        isSilverPlan: false,
        daysRemaining: fallbackBase,
        daysUsed: 0,
        planTier,
        planDurationDays: fallbackBase,
        canUseFeatures: true,
        allowedPages: planPages,
        canAccessPage: (path) => canAccessPage(path, false),
        requiresGoldPlan,
        goldOnlyPages: GOLD_ONLY_PAGES,
      };
    }

    const registrationDate = new Date(createdAt);
    const now = new Date(currentTimeMs);
    const diffTime = now - registrationDate;
    const daysUsed = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    // Prefer server-computed subscription data (already includes planExtraDays)
    const extraDays = parseInt(
      profile?.planExtraDays || user?.planExtraDays || 0,
      10,
    );
    const baseDays = resolveBaseDays(planTier, user, profile);
    const daysRemaining =
      subscription?.daysRemaining !== undefined
        ? Math.max(0, Number(subscription.daysRemaining) || 0)
        : Math.max(0, baseDays + extraDays - daysUsed);
    const isExpired = subscription?.isExpired === true || daysRemaining <= 0;

    return {
      isOnTrial: true,
      isExpired,
      hasPaidPlan: false,
      hasActivePlan: false,
      isGoldPlan: false,
      isSilverPlan: false,
      daysRemaining,
      daysUsed,
      planTier,
      planDurationDays: resolveBaseDays(planTier, user, profile),
      canUseFeatures: !isExpired,
      allowedPages: isExpired ? ["/payment"] : planPages,
      canAccessPage: (path) => canAccessPage(path, isExpired),
      requiresGoldPlan,
      goldOnlyPages: GOLD_ONLY_PAGES,
    };
  }, [user, profile, currentTimeMs]);

  return trialStatus;
};

export default useTrialStatus;
export { PLAN_PERMISSIONS, GOLD_ONLY_PAGES };
